import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Skeleton, SparkModalPortal, Modal as SparkModal, sparkToast } from '@spark';
import { data_from_server_post } from '@/shared/http/client';
import { TaskType } from '@/shared/domain/taskTypes';
import LabelExtractionManager from '@/features/datasets/components/LabelExtractionManager';

const uniqueNames = (...lists) => [...new Set(lists.flat().filter(Boolean))];

/**
 * ReplaceLabelModal component for bulk label replacement
 */
export const ReplaceLabelModal = ({
    visible,
    onCancel,
    onConfirm,
    allLabelOptions = null,
    taskCatalog = null,
    taskType,
    isMultilabel = false,
    clientId,
    llmUsecaseId,
    isBenchmark,
    filters,
    isSelectAll,
    excludedIds,
    includedIds,
    loading = false, // bulk operation loading
}) => {
    const modalTitle = useMemo(() => {
        if (taskType === TaskType.NER_WITH_RELATIONS) return 'Bulk replace entities and relations';
        if (taskType === TaskType.NER) return 'Bulk replace entities';
        if (taskType === TaskType.classification) return 'Bulk replace labels';
        return 'Bulk replace';
    }, [taskType]);

    const [loadingExtracted, setLoadingExtracted] = useState(false);
    const [extractedLabels, setExtractedLabels] = useState([]);
    const [extractedEntities, setExtractedEntities] = useState([]);
    const [extractedRelations, setExtractedRelations] = useState([]);

    const [labelMappings, setLabelMappings] = useState({});
    const [entityMappings, setEntityMappings] = useState({});
    const [relationMappings, setRelationMappings] = useState({});

    const classificationTaskLabels = useMemo(
        () => uniqueNames(taskCatalog?.label_names),
        [taskCatalog],
    );
    const entityTaskLabels = useMemo(
        () => uniqueNames(taskCatalog?.entity_names),
        [taskCatalog],
    );
    const relationTaskLabels = useMemo(
        () => uniqueNames(taskCatalog?.relation_names),
        [taskCatalog],
    );

    const classificationOptions = useMemo(
        () => uniqueNames(classificationTaskLabels, allLabelOptions?.label_names),
        [allLabelOptions, classificationTaskLabels],
    );
    const entityOptions = useMemo(
        () => uniqueNames(entityTaskLabels, allLabelOptions?.entity_names),
        [allLabelOptions, entityTaskLabels],
    );
    const relationOptions = useMemo(
        () => uniqueNames(relationTaskLabels, allLabelOptions?.relation_names),
        [allLabelOptions, relationTaskLabels],
    );

    // Prevent re-fetching extracted values on every parent re-render.
    // We compute a stable request signature and only fetch when it *meaningfully* changes.
    const bulkExtractRequestKey = useMemo(() => {
        if (!visible) return null;
        if (!clientId || !llmUsecaseId || !taskType) return null;
        return JSON.stringify({
            clientId,
            llmUsecaseId,
            taskType,
            isBenchmark: Boolean(isBenchmark),
            filters: filters && Object.keys(filters).length > 0 ? filters : null,
            isSelectAll: Boolean(isSelectAll),
            excludedIds: isSelectAll ? (excludedIds || []) : null,
            includedIds: !isSelectAll ? (includedIds || []) : null,
        });
    }, [visible, clientId, llmUsecaseId, taskType, isBenchmark, filters, isSelectAll, excludedIds, includedIds]);

    const lastFetchedKeyRef = useRef(null);
    useEffect(() => {
        if (!visible) {
            lastFetchedKeyRef.current = null;
        }
    }, [visible]);

    useEffect(() => {
        if (!bulkExtractRequestKey) return;
        // Avoid duplicate calls (including React 18 StrictMode double-invocation).
        if (lastFetchedKeyRef.current === bulkExtractRequestKey) return;
        lastFetchedKeyRef.current = bulkExtractRequestKey;

        setLoadingExtracted(true);

        const parsedKey = JSON.parse(bulkExtractRequestKey);
        data_from_server_post(
            'bulk-extracted-values',
            {
                client_id: parsedKey.clientId,
                llm_usecase_id: parsedKey.llmUsecaseId,
                is_benchmark: parsedKey.isBenchmark,
                filters: parsedKey.filters || undefined,
                is_select_all: parsedKey.isSelectAll,
                excluded_ids: parsedKey.isSelectAll ? parsedKey.excludedIds : undefined,
                included_ids: !parsedKey.isSelectAll ? parsedKey.includedIds : undefined,
            },
            (res) => {
                if (!res?.success) {
                    sparkToast.error(res?.error || 'Failed to load extracted values');
                    setExtractedLabels([]);
                    setExtractedEntities([]);
                    setExtractedRelations([]);
                    setLabelMappings({});
                    setEntityMappings({});
                    setRelationMappings({});
                    setLoadingExtracted(false);
                    return;
                }

                if (taskType === TaskType.classification) {
                    const labels = Array.isArray(res.labels) ? res.labels : [];
                    setExtractedLabels(labels);
                    const initial = {};
                    labels.forEach((l) => {
                        initial[l] = l;
                    });
                    setLabelMappings(initial);
                } else if (taskType === TaskType.NER) {
                    const entities = Array.isArray(res.entities) ? res.entities : [];
                    setExtractedEntities(entities);
                    const initial = {};
                    entities.forEach((l) => {
                        initial[l] = l;
                    });
                    setEntityMappings(initial);
                } else if (taskType === TaskType.NER_WITH_RELATIONS) {
                    const entities = Array.isArray(res.entities) ? res.entities : [];
                    const relations = Array.isArray(res.relations) ? res.relations : [];
                    setExtractedEntities(entities);
                    setExtractedRelations(relations);
                    const entInit = {};
                    entities.forEach((l) => {
                        entInit[l] = l;
                    });
                    const relInit = {};
                    relations.forEach((l) => {
                        relInit[l] = l;
                    });
                    setEntityMappings(entInit);
                    setRelationMappings(relInit);
                }
                setLoadingExtracted(false);
            },
            () => {
                sparkToast.error('Failed to load extracted values');
                setLoadingExtracted(false);
            }
        );
    }, [bulkExtractRequestKey, taskType]);

    const resetState = () => {
        setExtractedLabels([]);
        setExtractedEntities([]);
        setExtractedRelations([]);
        setLabelMappings({});
        setEntityMappings({});
        setRelationMappings({});
        setLoadingExtracted(false);
    };

    const handleCancel = () => {
        resetState();
        onCancel();
    };

    const handleConfirm = () => {
        if (loading || loadingExtracted) return;
        const compact = (mappingsObj) => {
            const out = {};
            Object.entries(mappingsObj || {}).forEach(([k, v]) => {
                if (v === null || v !== k) {
                    out[k] = v;
                }
            });
            return out;
        };

        const payload = {
            label_mappings: taskType === TaskType.classification ? compact(labelMappings) : undefined,
            entity_mappings:
                taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS ? compact(entityMappings) : undefined,
            relation_mappings: taskType === TaskType.NER_WITH_RELATIONS ? compact(relationMappings) : undefined,
        };

        const hasAnyChanges =
            (payload.label_mappings && Object.keys(payload.label_mappings).length > 0) ||
            (payload.entity_mappings && Object.keys(payload.entity_mappings).length > 0) ||
            (payload.relation_mappings && Object.keys(payload.relation_mappings).length > 0);

        if (!hasAnyChanges) {
            sparkToast.info('No changes to apply');
            handleCancel();
            return;
        }

        onConfirm(payload);
        resetState();
    };

    const isDoneDisabled = loading || loadingExtracted;

    return (
        <SparkModalPortal
            open={visible}
            onClose={handleCancel}
            destroyOnClose
            size="wide"
        >
            <SparkModal
                title={modalTitle}
                showSubtitle={false}
                showScrollBar={false}
                showFooter={true}
                showClose={true}
                primaryLabel="Done"
                primaryDisableWhenReadOnly
                tertiaryLabel="Cancel"
                onCloseClick={handleCancel}
                onPrimaryClick={isDoneDisabled ? undefined : handleConfirm}
                onTertiaryClick={handleCancel}
            >
            <div style={{ marginBottom: 12 }}>
                <p style={{ color: '#666', fontSize: 12, margin: 0 }}>
                    Values are computed from the currently filtered selection. Delete a row to remove completely the value from the filtered records.
                </p>
            </div>

            {loadingExtracted ? (
                <div style={{ padding: 24, display: 'flex', alignItems: 'center', gap: 12 }}>
                    <Skeleton shapeType="circle" width={24} height={24} />
                    <div>Loading extracted values...</div>
                </div>
            ) : null}

            {!loadingExtracted && taskType === TaskType.classification ? (
                <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                        Review Extracted Labels
                    </div>
                    <LabelExtractionManager
                        extractedLabels={extractedLabels}
                        loading={false}
                        labelMappings={labelMappings}
                        onDeleteLabel={(label) => {
                            setExtractedLabels((prev) => prev.filter((l) => l !== label));
                            setLabelMappings((prev) => {
                                // Mark as deleted so backend will remove occurrences (null => delete).
                                return { ...prev, [label]: null };
                            });
                        }}
                        onReplaceLabel={(oldLabel, newLabel) => {
                            setLabelMappings((prev) => ({ ...prev, [oldLabel]: newLabel }));
                        }}
                        onAddNewLabel={(newLabel) => {
                            setLabelMappings((prev) => ({ ...prev, [newLabel]: newLabel }));
                        }}
                        labelType="Label"
                        taskType={taskType}
                        isMultilabel={isMultilabel}
                        availableLabels={classificationOptions}
                        taskLabels={classificationTaskLabels}
                        title={null}
                    />
                </div>
            ) : null}

            {!loadingExtracted && (taskType === TaskType.NER || taskType === TaskType.NER_WITH_RELATIONS) ? (
                <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 12 }}>
                        Review Extracted Types
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                            Entities
                        </div>
                        <LabelExtractionManager
                            extractedLabels={extractedEntities}
                            loading={false}
                            labelMappings={entityMappings}
                            onDeleteLabel={(label) => {
                                setExtractedEntities((prev) => prev.filter((l) => l !== label));
                                setEntityMappings((prev) => {
                                    // Mark as deleted so backend will remove occurrences (null => delete).
                                    return { ...prev, [label]: null };
                                });
                            }}
                            onReplaceLabel={(oldLabel, newLabel) => {
                                setEntityMappings((prev) => ({ ...prev, [oldLabel]: newLabel }));
                            }}
                            onAddNewLabel={(newLabel) => {
                                setEntityMappings((prev) => ({ ...prev, [newLabel]: newLabel }));
                            }}
                            labelType="Entity"
                            taskType={taskType}
                            isMultilabel={false}
                            availableLabels={entityOptions}
                            taskLabels={entityTaskLabels}
                            title={null}
                        />
                    </div>

                    {taskType === TaskType.NER_WITH_RELATIONS ? (
                        <div style={{ marginTop: 16 }}>
                            <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 8 }}>
                                Relations
                            </div>
                            <LabelExtractionManager
                                extractedLabels={extractedRelations}
                                loading={false}
                                labelMappings={relationMappings}
                                onDeleteLabel={(label) => {
                                    setExtractedRelations((prev) => prev.filter((l) => l !== label));
                                    setRelationMappings((prev) => {
                                        // Mark as deleted so backend will remove occurrences (null => delete).
                                        return { ...prev, [label]: null };
                                    });
                                }}
                                onReplaceLabel={(oldLabel, newLabel) => {
                                    setRelationMappings((prev) => ({ ...prev, [oldLabel]: newLabel }));
                                }}
                                onAddNewLabel={(newLabel) => {
                                    setRelationMappings((prev) => ({ ...prev, [newLabel]: newLabel }));
                                }}
                                labelType="Relation"
                                taskType={taskType}
                                isMultilabel={false}
                                availableLabels={relationOptions}
                                taskLabels={relationTaskLabels}
                                title={null}
                            />
                        </div>
                    ) : null}
                </div>
            ) : null}
            </SparkModal>
        </SparkModalPortal>
    );
};

